/**
 * O mapa em si: um `google.maps.Map` com marcadores avançados.
 *
 * Só desenha. Quem decide o que entra (reciprocidade, filtro, dia) é a tela.
 * Sem Google (sem chave, sem rede) mostra um quadro dizendo isso — a lista
 * abaixo dele continua funcionando.
 *
 * `colorScheme` é fixado na criação do mapa: mudar o tema recria o mapa
 * (a `key` no elemento faz isso do lado de quem usa).
 */
import { useEffect, useRef, useState } from 'react';

import { carregarGoogleMaps } from '../../maps/google';
import type { PessoaNoMapa } from './posicoes';
import { frescor, haQuanto, iniciais, type Ponto } from './regras';

export interface LugarNoMapa {
  id: string;
  titulo: string;
  latitude: number;
  longitude: number;
  tipo: 'atividade' | 'hospedagem' | 'encontro' | 'carro';
  /** Horário (atividade), para a etiqueta. */
  hora?: string;
}

interface Props {
  mapId: string | null;
  tema: 'light' | 'dark';
  pessoas: PessoaNoMapa[];
  lugares: LugarNoMapa[];
  /** Cor da família por memberId, para o anel do pin. */
  cores: Record<string, string | undefined>;
  /** Foto da conta por membro: o pino mostra a pessoa, não só as iniciais. */
  fotos?: Record<string, string | null | undefined>;
  agora: number;
  /** Incrementa para pedir "enquadrar tudo" de novo. */
  enquadrar: number;
  /** Ponto para centralizar (minha posição), quando pedido. */
  centralizarEm: (Ponto & { pedido: number }) | null;
  onPronto?: (disponivel: boolean) => void;
}

type Marcador = google.maps.marker.AdvancedMarkerElement;

function conteudoDePessoa(p: PessoaNoMapa, cor: string | undefined, foto: string | null | undefined, agora: number): HTMLElement {
  const el = document.createElement('div');
  const estado = frescor(p.recordedAt, agora);
  el.className = `pin pin--pessoa pin--${estado}${p.me ? ' pin--eu' : ''}`;
  el.style.setProperty('--pin-cor', cor ?? 'var(--brand)');
  const avatar = document.createElement('span');
  avatar.className = 'pin__avatar';
  if (foto) {
    const img = document.createElement('img');
    img.src = foto;
    img.alt = '';
    avatar.append(img);
  } else avatar.textContent = iniciais(p.name);
  // Refino final: a etiqueta "agora / há N min" fica presa ao pino.
  const quando = document.createElement('span');
  quando.className = 'pin__quando';
  quando.textContent = haQuanto(p.recordedAt, agora);
  el.append(avatar, quando);
  el.title = p.name;
  return el;
}

function conteudoDeLugar(l: LugarNoMapa): HTMLElement {
  const el = document.createElement('div');
  el.className = `pin pin--lugar pin--${l.tipo}`;
  el.innerHTML = `<span class="pin__icone">${l.tipo === 'hospedagem' ? '⌂' : l.tipo === 'encontro' ? '★' : l.tipo === 'carro' ? 'P' : '◆'}</span><span class="pin__rotulo"></span>`;
  // Atividade: pino quadrado da marca com a hora na etiqueta; os demais, o nome.
  (el.querySelector('.pin__rotulo') as HTMLElement).textContent = l.tipo === 'atividade' && l.hora ? l.hora : l.hora ? `${l.hora} · ${l.titulo}` : l.titulo;
  el.title = l.titulo;
  return el;
}

export function MapaGoogle({ mapId, tema, pessoas, lugares, cores, fotos, agora, enquadrar, centralizarEm, onPronto }: Props) {
  const caixa = useRef<HTMLDivElement>(null);
  const mapa = useRef<google.maps.Map | null>(null);
  const marcadores = useRef<Map<string, Marcador>>(new Map());
  const construtor = useRef<typeof google.maps.marker.AdvancedMarkerElement | null>(null);
  const [disponivel, setDisponivel] = useState<boolean | null>(null);
  const jaEnquadrou = useRef(false);

  // Criação do mapa — uma vez por montagem (o tema entra pela key).
  useEffect(() => {
    let vivo = true;
    void (async () => {
      const g = await carregarGoogleMaps();
      if (!vivo) return;
      if (!g || !caixa.current) {
        setDisponivel(false);
        onPronto?.(false);
        return;
      }
      const { Map: GMap } = (await g.maps.importLibrary('maps')) as google.maps.MapsLibrary;
      const { AdvancedMarkerElement } = (await g.maps.importLibrary('marker')) as google.maps.MarkerLibrary;
      if (!vivo || !caixa.current) return;
      construtor.current = AdvancedMarkerElement;
      mapa.current = new GMap(caixa.current, {
        // Sem mapId os marcadores avançados não desenham; com ele, o estilo
        // claro/escuro vem da nuvem e `colorScheme` escolhe qual.
        ...(mapId ? { mapId } : {}),
        colorScheme: tema === 'dark' ? g.maps.ColorScheme.DARK : g.maps.ColorScheme.LIGHT,
        center: { lat: 0, lng: 0 },
        zoom: 2,
        disableDefaultUI: true,
        zoomControl: false,
        gestureHandling: 'greedy',
        clickableIcons: false,
      });
      setDisponivel(true);
      onPronto?.(true);
    })();
    return () => {
      vivo = false;
      marcadores.current.forEach((m) => {
        m.map = null;
      });
      marcadores.current.clear();
      mapa.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Marcadores: reconcilia por id — cria, atualiza posição/conteúdo, remove.
  useEffect(() => {
    const m = mapa.current;
    const Marker = construtor.current;
    if (!m || !Marker || !disponivel) return;

    const vivos = new Set<string>();
    const por = (id: string, posicao: Ponto, conteudo: HTMLElement) => {
      vivos.add(id);
      const existente = marcadores.current.get(id);
      if (existente) {
        existente.position = { lat: posicao.latitude, lng: posicao.longitude };
        existente.content = conteudo;
        return;
      }
      marcadores.current.set(
        id,
        new Marker({ map: m, position: { lat: posicao.latitude, lng: posicao.longitude }, content: conteudo }),
      );
    };
    for (const l of lugares) por(`lugar:${l.id}`, l, conteudoDeLugar(l));
    for (const p of pessoas) por(`pessoa:${p.memberId}`, p, conteudoDePessoa(p, cores[p.memberId], fotos?.[p.memberId], agora));

    for (const [id, marcador] of marcadores.current) {
      if (!vivos.has(id)) {
        marcador.map = null;
        marcadores.current.delete(id);
      }
    }

    // Primeiro enquadramento assim que houver o que mostrar.
    if (!jaEnquadrou.current && vivos.size > 0) {
      jaEnquadrou.current = true;
      enquadrarTudo(m, [...pessoas, ...lugares]);
    }
  }, [pessoas, lugares, cores, fotos, agora, disponivel]);

  useEffect(() => {
    if (!mapa.current || !disponivel || enquadrar === 0) return;
    enquadrarTudo(mapa.current, [...pessoas, ...lugares]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enquadrar]);

  useEffect(() => {
    if (!mapa.current || !disponivel || !centralizarEm) return;
    mapa.current.panTo({ lat: centralizarEm.latitude, lng: centralizarEm.longitude });
    if ((mapa.current.getZoom() ?? 0) < 15) mapa.current.setZoom(15);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centralizarEm?.pedido]);

  if (disponivel === false) {
    return (
      <div className="mapa__quadro mapa__quadro--vazio" role="img" aria-label="Mapa indisponível">
        <p>Mapa indisponível neste ambiente.</p>
        <p className="mapa__quadro-nota">A lista abaixo continua valendo.</p>
      </div>
    );
  }
  return <div ref={caixa} className="mapa__quadro" role="application" aria-label="Mapa do grupo" />;
}

function enquadrarTudo(m: google.maps.Map, pontos: Ponto[]) {
  if (pontos.length === 0) return;
  if (pontos.length === 1) {
    const p = pontos[0]!;
    m.setCenter({ lat: p.latitude, lng: p.longitude });
    m.setZoom(15);
    return;
  }
  const b = new google.maps.LatLngBounds();
  for (const p of pontos) b.extend({ lat: p.latitude, lng: p.longitude });
  m.fitBounds(b, 48);
}
